/**
 * PhotoUpload Component
 *
 * Handles photo upload for recipes with preview functionality.
 * Shows a placeholder gradient when no photo is selected.
 */

import { useRef, useEffect } from 'react';

interface PhotoUploadProps {
  value: File | null;
  existingUrl: string | null;
  onChange: (file: File | null) => void;
}

/**
 * Camera icon for upload button
 */
function CameraIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
      />
    </svg>
  );
}

/**
 * Plate icon for placeholder
 */
function PlateIcon() {
  return (
    <svg
      className="w-12 h-12 text-white/80"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.17c0 .62-.504 1.124-1.125 1.124H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12M12.265 3.11a.375.375 0 11-.53 0L12 2.845l.265.265zm-3 0a.375.375 0 11-.53 0L9 2.845l.265.265zm6 0a.375.375 0 11-.53 0L15 2.845l.265.265z"
      />
    </svg>
  );
}

/**
 * X icon for clear button
 */
function XIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function PhotoUpload({ value, existingUrl, onChange }: PhotoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Listen for paste events to allow pasting images from clipboard
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            onChange(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onChange(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Determine what to show in preview
  const previewUrl = value ? URL.createObjectURL(value) : existingUrl;

  return (
    <div className="flex gap-5 items-start">
      {/* Preview area */}
      <div className="w-[200px] h-[150px] rounded-xl overflow-hidden flex-shrink-0">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Recipe photo preview"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <PlateIcon />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
          aria-label="Upload photo"
        />

        <button
          type="button"
          onClick={handleUploadClick}
          className="inline-flex items-center gap-2 px-5 py-3 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 hover:border-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
        >
          <CameraIcon />
          <span className="text-sm font-medium text-gray-700">Upload Photo</span>
        </button>

        {(value || existingUrl) && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
          >
            <XIcon />
            <span>Remove photo</span>
          </button>
        )}

        <p className="text-xs text-gray-400">
          Recommended: 1200x800px, max 10MB
          <br />
          Formats: JPG, PNG, WebP
          <br />
          <span className="text-gray-500">Tip: You can also paste an image (Cmd/Ctrl+V)</span>
        </p>
      </div>
    </div>
  );
}
